import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableFooter,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ExpandMore, UploadFile } from "@mui/icons-material";
import * as moment from "moment";
import colors from "assets/theme/base/colors";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import ApiCalls from "services/flick_erp_api_call";

const offerStatusLabels = {
  created: "Created",
  sent: "Sent",
  accepted: "Accepted",
  rejected: "Rejected",
  cancelled: "Cancelled",
  expired: "Expired",
  documents_requested: "Documents Requested",
  documents_submitted: "Documents Submitted",
  documents_verified: "Documents Verified",
  ready_to_join: "Ready To Join",
  joined: "Joined",
  1: "Created",
  2: "Sent",
  3: "Rejected",
  4: "Accepted",
  5: "Joined",
};

const terminalStatuses = ["rejected", "cancelled", "expired", "joined"];
const getOfferStatusLabel = (status) => offerStatusLabels[status] || status || "Unknown";

const normalizeCollection = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const statusAliases = {
  "1": "created",
  "2": "sent",
  "3": "rejected",
  "4": "accepted",
  "5": "joined",
};

const normalizeStatusValue = (offer) => {
  const rawStatus = String(offer?.status_display || offer?.status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

  return statusAliases[rawStatus] || rawStatus;
};

const getStatusChipColor = (status) => {
  if (["accepted", "documents_verified", "ready_to_join", "joined"].includes(status)) {
    return "success";
  }
  if (["rejected", "cancelled", "expired"].includes(status)) {
    return "error";
  }
  if (["documents_requested", "documents_submitted"].includes(status)) {
    return "warning";
  }
  return "info";
};

const formatDateTime = (value, withTime = true) =>
  value ? moment(value).format(withTime ? "DD MMM YYYY, hh:mm A" : "DD MMM YYYY") : "Not available";

const getDocumentTypeLabel = (doc) =>
  doc?.document_type_name ||
  doc?.document_type?.name ||
  doc?.document_name ||
  `Document ${doc?.document_type || doc?.document_type_id || ""}`.trim();

const htmlPreviewKeys = [
  "email_html",
  "rendered_email_html",
  "offer_letter_html",
  "document_html",
  "rendered_document_html",
  "letter_html",
];

const pdfPreviewKeys = [
  "pdf",
  "pdf_url",
  "offer_pdf",
  "offer_pdf_url",
  "document_pdf",
];

const getPreviewHtmlSections = (payload) =>
  htmlPreviewKeys
    .filter((key) => payload?.[key])
    .map((key) => ({
      key,
      label:
        key === "email_html" || key === "rendered_email_html"
          ? "Rendered Email"
          : "Offer Letter Preview",
      html: payload[key],
    }));

const getPreviewPdfUrl = (payload) =>
  pdfPreviewKeys.map((key) => payload?.[key]).find(Boolean) || "";

export default function JobOffer() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isDarkMode = theme.palette.mode === "dark";
  const surfaceColor = isDarkMode ? theme.palette.background.paper : "#fff";
  const mutedSurfaceColor = isDarkMode ? theme.palette.background.default : colors.background.accent;
  const borderColor = isDarkMode ? theme.palette.divider : `${colors.secondary.main}30`;

  const [jobOffers, setJobOffers] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedJobOffer, setSelectedJobOffer] = useState(null);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [documentsDialog, setDocumentsDialog] = useState(false);
  const [remindersDialog, setRemindersDialog] = useState(false);
  const [sendDialog, setSendDialog] = useState(false);
  const [requestDocumentsDialog, setRequestDocumentsDialog] = useState(false);
  const [uploadDocumentDialog, setUploadDocumentDialog] = useState(false);
  const [verifyDocumentDialog, setVerifyDocumentDialog] = useState(false);
  const [timelineItems, setTimelineItems] = useState([]);
  const [offerDocuments, setOfferDocuments] = useState([]);
  const [reasonDialog, setReasonDialog] = useState({ open: false, action: "", title: "" });
  const [reason, setReason] = useState("");
  const [sendOfferError, setSendOfferError] = useState("");
  const [sendOfferData, setSendOfferData] = useState({
    use_default_templates: true,
    email_template: "",
    document_template: "",
  });
  const [documentRequestData, setDocumentRequestData] = useState({
    document_type_ids: [],
    deadline: "",
  });
  const [documentUploadData, setDocumentUploadData] = useState({
    document_type: "",
    file: null,
  });
  const [documentVerifyData, setDocumentVerifyData] = useState({
    document_type: "",
    verified: true,
    rejected_reason: "",
  });

  useEffect(() => {
    getJobOffers();
    getDocumentTypes();
  }, []);

  async function getJobOffers() {
    const res = await ApiCalls.getJobOffers();
    if (res?.success && res?.code === 200) {
      setJobOffers(normalizeCollection(res.data));
    }
  }

  async function getDocumentTypes() {
    const res = await ApiCalls.getDocuments();
    if (res?.success && res?.code === 200) {
      setDocumentTypes(normalizeCollection(res.data));
    }
  }

  const paginatedOffers = useMemo(
    () => (rowsPerPage > 0 ? jobOffers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : jobOffers),
    [jobOffers, page, rowsPerPage]
  );

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const resetDocumentsPayloads = () => {
    setDocumentRequestData({ document_type_ids: [], deadline: "" });
    setDocumentUploadData({ document_type: "", file: null });
    setDocumentVerifyData({ document_type: "", verified: true, rejected_reason: "" });
  };

  const closeCollectionDialogs = () => {
    setHistoryDialog(false);
    setDocumentsDialog(false);
    setRemindersDialog(false);
    setPreviewDialog(false);
    setSendDialog(false);
    setRequestDocumentsDialog(false);
    setUploadDocumentDialog(false);
    setVerifyDocumentDialog(false);
    setPreviewData(null);
    setTimelineItems([]);
    setOfferDocuments([]);
    setSelectedJobOffer(null);
    setSendOfferError("");
    setSendOfferData({
      use_default_templates: true,
      email_template: "",
      document_template: "",
    });
    resetDocumentsPayloads();
  };

  const openReasonDialog = (offer, action, title) => {
    setSelectedJobOffer(offer);
    setReason("");
    setReasonDialog({ open: true, action, title });
  };

  const closeReasonDialog = () => {
    setReasonDialog({ open: false, action: "", title: "" });
    setSelectedJobOffer(null);
    setReason("");
  };

  const handlePreview = async (offer) => {
    setSelectedJobOffer(offer);
    const res = await ApiCalls.previewJobOffer(offer.uuid, {});
    if (res?.success && res?.code === 200) {
      setPreviewData(res.data);
      setPreviewDialog(true);
    }
  };

  const openSendDialog = (offer) => {
    setSelectedJobOffer(offer);
    setSendOfferError("");
    setSendOfferData({
      use_default_templates: true,
      email_template: "",
      document_template: "",
    });
    setSendDialog(true);
  };

  const handleSend = async () => {
    if (!selectedJobOffer?.uuid) return;

    if (
      !sendOfferData.use_default_templates &&
      (!sendOfferData.email_template || !sendOfferData.document_template)
    ) {
      setSendOfferError("Select both email and document templates or use default templates.");
      return;
    }

    const payload = sendOfferData.use_default_templates
      ? {}
      : {
          email_template: Number(sendOfferData.email_template),
          document_template: Number(sendOfferData.document_template),
        };

    const res = await ApiCalls.sendJobOffer(selectedJobOffer.uuid, payload);
    if (res?.success) {
      closeCollectionDialogs();
      getJobOffers();
    }
  };

  const handleAccept = async (offer) => {
    const res = await ApiCalls.acceptJobOffer(offer.uuid, {});
    if (res?.success) {
      getJobOffers();
    }
  };

  const handleMarkReadyToJoin = async (offer) => {
    const res = await ApiCalls.markJobOfferReadyToJoin(offer.uuid, {});
    if (res?.success) {
      getJobOffers();
    }
  };

  const handleMarkJoined = async (offer) => {
    const res = await ApiCalls.markJobOfferJoined(offer.uuid, {});
    if (res?.success) {
      getJobOffers();
    }
  };

  const handleReasonSubmit = async () => {
    if (!reason.trim() || !selectedJobOffer?.uuid) return;

    const payload = { reason: reason.trim() };
    let res = null;

    if (reasonDialog.action === "reject") {
      res = await ApiCalls.rejectJobOfferLifecycle(selectedJobOffer.uuid, payload);
    }
    if (reasonDialog.action === "cancel") {
      res = await ApiCalls.cancelJobOfferLifecycle(selectedJobOffer.uuid, payload);
    }
    if (reasonDialog.action === "expire") {
      res = await ApiCalls.expireJobOfferLifecycle(selectedJobOffer.uuid, payload);
    }

    if (res?.success) {
      closeReasonDialog();
      getJobOffers();
    }
  };

  const fetchOfferDocuments = async (offer) => {
    if (!offer?.uuid) return [];
    const res = await ApiCalls.getJobOfferDocuments(offer.uuid);
    if (res?.success) {
      const documents = normalizeCollection(res.data);
      setOfferDocuments(documents);
      return documents;
    }
    setOfferDocuments([]);
    return [];
  };

  const handleOpenCollection = async (offer, kind) => {
    setSelectedJobOffer(offer);
    let res = null;

    if (kind === "history") {
      res = await ApiCalls.getJobOfferStatusHistory(offer.uuid);
      if (res?.success) {
        setTimelineItems(normalizeCollection(res.data));
        setHistoryDialog(true);
      }
    }

    if (kind === "documents") {
      res = await ApiCalls.getJobOfferDocuments(offer.uuid);
      if (res?.success) {
        const documents = normalizeCollection(res.data);
        setOfferDocuments(documents);
        setTimelineItems(documents);
        setDocumentsDialog(true);
      }
    }

    if (kind === "reminders") {
      res = await ApiCalls.getJobOfferReminderLogs(offer.uuid);
      if (res?.success) {
        setTimelineItems(normalizeCollection(res.data));
        setRemindersDialog(true);
      }
    }
  };

  const openDocumentDialog = async (offer, type) => {
    setSelectedJobOffer(offer);
    resetDocumentsPayloads();
    if (type === "upload" || type === "verify") {
      await fetchOfferDocuments(offer);
    }
    if (type === "request") setRequestDocumentsDialog(true);
    if (type === "upload") setUploadDocumentDialog(true);
    if (type === "verify") setVerifyDocumentDialog(true);
  };

  const handleRequestDocuments = async () => {
    if (!selectedJobOffer?.uuid) return;
    if (!documentRequestData.document_type_ids.length || !documentRequestData.deadline) return;

    const res = await ApiCalls.requestJobOfferDocuments(selectedJobOffer.uuid, {
      document_type_ids: documentRequestData.document_type_ids,
      deadline: moment(documentRequestData.deadline).format(),
    });

    if (res?.success) {
      closeCollectionDialogs();
      getJobOffers();
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedJobOffer?.uuid || !documentUploadData.document_type || !documentUploadData.file) return;

    const formData = new FormData();
    formData.append("document_type", documentUploadData.document_type);
    formData.append("file", documentUploadData.file);

    const res = await ApiCalls.uploadJobOfferDocument(selectedJobOffer.uuid, formData, true);
    if (res?.success) {
      closeCollectionDialogs();
      getJobOffers();
    }
  };

  const handleVerifyDocument = async () => {
    if (!selectedJobOffer?.uuid || !documentVerifyData.document_type) return;
    if (!documentVerifyData.verified && !documentVerifyData.rejected_reason.trim()) return;

    const res = await ApiCalls.verifyJobOfferDocument(selectedJobOffer.uuid, {
      document_type: documentVerifyData.document_type,
      verified: documentVerifyData.verified,
      ...(documentVerifyData.verified ? {} : { rejected_reason: documentVerifyData.rejected_reason.trim() }),
    });

    if (res?.success) {
      closeCollectionDialogs();
      getJobOffers();
    }
  };

  const getAvailableDocumentTypeOptions = (offer) => {
    const sourceDocuments = offerDocuments.length ? offerDocuments : normalizeCollection(offer?.documents);
    if (!sourceDocuments.length) return documentTypes;

    const requestedIds = sourceDocuments
      .map((doc) => doc?.document_type?.id || doc?.document_type_id || doc?.document_type)
      .filter(Boolean);

    return documentTypes.filter((docType) => requestedIds.includes(docType.id));
  };

  const canSend = (status) => status === "created";
  const canAccept = (status) => status === "sent";
  const canRequestDocuments = (status) => status === "accepted";
  const canUploadDocuments = (status) => ["documents_requested", "documents_submitted"].includes(status);
  const canVerifyDocuments = (status) => ["documents_requested", "documents_submitted"].includes(status);
  const canMarkReady = (status) => status === "documents_verified";
  const canMarkJoinedState = (status) => status === "ready_to_join";
  const canEndLifecycle = (status) => !terminalStatuses.includes(status);

  return (
    <MDBox>
      <Card
        sx={{
          boxShadow: "none",
          borderRadius: "24px",
          border: `1px solid ${borderColor}`,
          backgroundColor: surfaceColor,
        }}
      >
        <Box
          px={2.5}
          py={2.25}
          sx={{
            borderBottom: `1px solid ${borderColor}`,
            background: isDarkMode
              ? "linear-gradient(135deg, rgba(95,81,168,0.22), rgba(15,23,42,0.1))"
              : "linear-gradient(135deg, rgba(103,80,164,0.14), rgba(255,255,255,0.96))",
          }}
        >
          <MDTypography variant="h5" fontWeight="bold">
            Job Offers
          </MDTypography>
          <MDTypography variant="button" color="text">
            Manage the full offer lifecycle from offer creation through document verification and joining.
          </MDTypography>
        </Box>

        {jobOffers.length ? (
          <Box display="flex" flexDirection="column" gap={1.5} px={2} py={2}>
            {paginatedOffers.map((offer) => {
              const normalizedStatus = normalizeStatusValue(offer);
              const requestedDocumentTypes = getAvailableDocumentTypeOptions(offer);
              const documentsCount =
                normalizeCollection(offer?.documents).length ||
                offer?.document_count ||
                offer?.documents_count ||
                0;
              const previewSections = getPreviewHtmlSections(offer);
              const offerPdfUrl = getPreviewPdfUrl(offer);

              return (
                <Accordion
                  key={offer.uuid}
                  disableGutters
                  sx={{
                    borderRadius: "20px !important",
                    overflow: "hidden",
                    boxShadow: "none",
                    border: `1px solid ${borderColor}`,
                    backgroundColor: surfaceColor,
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box
                      width="100%"
                      display="flex"
                      flexDirection={isSmallScreen ? "column" : "row"}
                      justifyContent="space-between"
                      alignItems={isSmallScreen ? "flex-start" : "center"}
                      gap={1.5}
                    >
                      <Box>
                        <Typography fontWeight={700}>
                          {offer?.candidate?.full_name || "Candidate"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {offer?.candidate?.email || "No email"} • Expected DOJ: {formatDateTime(offer?.expected_doj, false)}
                        </Typography>
                      </Box>
                      <Chip
                        label={getOfferStatusLabel(normalizedStatus)}
                        color={getStatusChipColor(normalizedStatus)}
                        size="small"
                      />
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ backgroundColor: mutedSurfaceColor }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={5}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: "18px",
                            backgroundColor: surfaceColor,
                            border: `1px solid ${borderColor}`,
                            height: "100%",
                          }}
                        >
                          <Typography fontWeight={700} mb={1.5}>
                            Offer Snapshot
                          </Typography>
                          <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">CTC</Typography>
                              <Typography fontWeight={600}>{offer?.ctc || "Not set"}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Active</Typography>
                              <Typography fontWeight={600}>{offer?.active ? "Yes" : "No"}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Offer Valid Till</Typography>
                              <Typography fontWeight={600}>{formatDateTime(offer?.valid_till, false)}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Document Deadline</Typography>
                              <Typography fontWeight={600}>{formatDateTime(offer?.document_deadline, false)}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Documents</Typography>
                              <Typography fontWeight={600}>{documentsCount}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Created</Typography>
                              <Typography fontWeight={600}>{formatDateTime(offer?.created_at)}</Typography>
                            </Grid>
                          </Grid>

                          {previewSections.length || offerPdfUrl ? (
                            <>
                              <Divider sx={{ my: 2 }} />
                              <Typography fontWeight={700} mb={1}>
                                Generated Assets
                              </Typography>
                              <Box display="flex" flexWrap="wrap" gap={1}>
                                {previewSections.length ? (
                                  <Chip
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    label={`${previewSections.length} preview${previewSections.length > 1 ? "s" : ""} available`}
                                  />
                                ) : null}
                                {offerPdfUrl ? (
                                  <MDButton
                                    size="small"
                                    variant="text"
                                    color="info"
                                    component="a"
                                    href={offerPdfUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Open PDF
                                  </MDButton>
                                ) : null}
                              </Box>
                            </>
                          ) : null}

                          <Divider sx={{ my: 2 }} />

                          <Box display="flex" flexWrap="wrap" gap={1}>
                            <MDButton size="small" variant="text" color="info" onClick={() => handleOpenCollection(offer, "history")}>
                              Status History
                            </MDButton>
                            <MDButton size="small" variant="text" color="info" onClick={() => handleOpenCollection(offer, "documents")}>
                              Documents
                            </MDButton>
                            <MDButton size="small" variant="text" color="info" onClick={() => handleOpenCollection(offer, "reminders")}>
                              Reminder Logs
                            </MDButton>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={7}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: "18px",
                            backgroundColor: surfaceColor,
                            border: `1px solid ${borderColor}`,
                          }}
                        >
                          <Typography fontWeight={700} mb={1.5}>
                            Offer Actions
                          </Typography>

                          <Box display="flex" flexWrap="wrap" gap={1} mb={1.5}>
                            <MDButton size="small" variant="outlined" color="info" onClick={() => handlePreview(offer)}>
                              Preview
                            </MDButton>
                            <MDButton size="small" variant="outlined" color="success" disabled={!canSend(normalizedStatus)} onClick={() => openSendDialog(offer)}>
                              Send
                            </MDButton>
                            <MDButton size="small" variant="outlined" color="success" disabled={!canAccept(normalizedStatus)} onClick={() => handleAccept(offer)}>
                              Accept
                            </MDButton>
                            <MDButton
                              size="small"
                              variant="outlined"
                              color="warning"
                              disabled={!canEndLifecycle(normalizedStatus)}
                              onClick={() => openReasonDialog(offer, "reject", "Reject Offer")}
                            >
                              Reject
                            </MDButton>
                            <MDButton
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={!canEndLifecycle(normalizedStatus)}
                              onClick={() => openReasonDialog(offer, "cancel", "Cancel Offer")}
                            >
                              Cancel
                            </MDButton>
                            <MDButton
                              size="small"
                              variant="outlined"
                              color="dark"
                              disabled={!canEndLifecycle(normalizedStatus)}
                              onClick={() => openReasonDialog(offer, "expire", "Expire Offer")}
                            >
                              Expire
                            </MDButton>
                          </Box>

                          <Divider sx={{ my: 1.5 }} />

                          <Typography variant="button" color="text" fontWeight="medium">
                            Document Lifecycle
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1} mt={1.25}>
                            <MDButton
                              size="small"
                              variant="contained"
                              color="info"
                              disabled={!canRequestDocuments(normalizedStatus)}
                              onClick={() => openDocumentDialog(offer, "request")}
                            >
                              Request Documents
                            </MDButton>
                            <MDButton
                              size="small"
                              variant="outlined"
                              color="info"
                              startIcon={<UploadFile />}
                              disabled={!canUploadDocuments(normalizedStatus) || !requestedDocumentTypes.length}
                              onClick={() => openDocumentDialog(offer, "upload")}
                            >
                              Upload Document
                            </MDButton>
                            <MDButton
                              size="small"
                              variant="outlined"
                              color="success"
                              disabled={!canVerifyDocuments(normalizedStatus) || !requestedDocumentTypes.length}
                              onClick={() => openDocumentDialog(offer, "verify")}
                            >
                              Verify Document
                            </MDButton>
                            <MDButton
                              size="small"
                              variant="outlined"
                              color="success"
                              disabled={!canMarkReady(normalizedStatus)}
                              onClick={() => handleMarkReadyToJoin(offer)}
                            >
                              Mark Ready To Join
                            </MDButton>
                            <MDButton
                              size="small"
                              variant="contained"
                              color="success"
                              disabled={!canMarkJoinedState(normalizedStatus)}
                              onClick={() => handleMarkJoined(offer)}
                            >
                              Mark Joined
                            </MDButton>
                          </Box>

                          <Box
                            mt={2}
                            px={1.5}
                            py={1.25}
                            sx={{
                              borderRadius: "14px",
                              border: `1px dashed ${borderColor}`,
                              backgroundColor: isDarkMode ? "rgba(255,255,255,0.02)" : "rgba(103,80,164,0.04)",
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              Flow guidance: Accept the offer first, then request documents. Once all requested documents are uploaded and verified, move the candidate to ready-to-join and finally mark joined.
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}

            <Paper sx={{ borderRadius: "18px", overflow: "hidden", backgroundColor: surfaceColor }}>
              <Table>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                      colSpan={8}
                      count={jobOffers.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </Paper>
          </Box>
        ) : (
          <Card
            sx={{
              m: 3,
              p: 5,
              pt: 7,
              borderRadius: "24px",
              textAlign: "center",
              border: `1px dashed ${colors.secondary.main}40`,
              backgroundColor: mutedSurfaceColor,
              boxShadow: "none",
            }}
          >
            <MDTypography variant="h5" sx={{ mb: 1 }}>
              No offers have been rolled out yet.
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Candidates become eligible for offers once the final interview outcome is complete.
            </MDTypography>
          </Card>
        )}
      </Card>

      <Dialog open={previewDialog} onClose={closeCollectionDialogs} PaperProps={{ sx: { width: "920px", maxWidth: "96vw", borderRadius: "20px" } }}>
        <DialogTitle>Offer Preview</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {previewData?.message || "Preview generated."}
          </Typography>
          {previewData?.subject ? <Typography mt={2}>Subject: {previewData.subject}</Typography> : null}
          {getPreviewPdfUrl(previewData) ? (
            <Box mt={2}>
              <MDButton
                variant="outlined"
                color="info"
                component="a"
                href={getPreviewPdfUrl(previewData)}
                target="_blank"
                rel="noreferrer"
              >
                Open Generated PDF
              </MDButton>
            </Box>
          ) : null}
          {getPreviewHtmlSections(previewData).length ? (
            <Box display="flex" flexDirection="column" gap={2} mt={2}>
              {getPreviewHtmlSections(previewData).map((section) => (
                <Box key={section.key} p={2} sx={{ borderRadius: "16px", border: `1px solid ${borderColor}` }}>
                  <Typography variant="caption" color="text.secondary">
                    {section.label}
                  </Typography>
                  <Box
                    mt={1.5}
                    sx={{
                      borderRadius: "12px",
                      overflow: "hidden",
                      backgroundColor: surfaceColor,
                      border: `1px solid ${borderColor}`,
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        maxHeight: "52vh",
                        overflowY: "auto",
                        "& *": {
                          color: `${theme.palette.text.primary} !important`,
                        },
                        "& a": {
                          color: `${colors.info.main} !important`,
                        },
                      }}
                      dangerouslySetInnerHTML={{ __html: section.html }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box mt={2} p={2} sx={{ borderRadius: "16px", border: `1px dashed ${borderColor}` }}>
              <Typography variant="body2" color="text.secondary">
                No rendered preview HTML was returned by the API for this offer yet.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="dark" onClick={closeCollectionDialogs}>Close</MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={sendDialog} onClose={closeCollectionDialogs} PaperProps={{ sx: { width: "720px", maxWidth: "95vw", borderRadius: "22px" } }}>
        <DialogTitle>
          <Typography variant="h5" fontWeight={700}>
            Send Offer To Candidate
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review the delivery setup before sending the offer. You can use the organization defaults or specify template IDs for the job-offer email and letter.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Box
              sx={{
                p: 2,
                borderRadius: "16px",
                border: `1px solid ${borderColor}`,
                backgroundColor: mutedSurfaceColor,
              }}
            >
              <Typography fontWeight={700}>
                {selectedJobOffer?.candidate?.full_name || "Candidate"}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {selectedJobOffer?.candidate?.email || "No email available"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Expected DOJ: {formatDateTime(selectedJobOffer?.expected_doj, false)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CTC: {selectedJobOffer?.ctc || "Not set"}
              </Typography>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={sendOfferData.use_default_templates}
                  onChange={(event) =>
                    setSendOfferData((prev) => ({
                      ...prev,
                      use_default_templates: event.target.checked,
                    }))
                  }
                />
              }
              label="Use organization default templates"
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Email Template ID"
                  disabled={sendOfferData.use_default_templates}
                  value={sendOfferData.email_template}
                  onChange={(event) =>
                    setSendOfferData((prev) => ({
                      ...prev,
                      email_template: event.target.value,
                    }))
                  }
                  helperText={
                    sendOfferData.use_default_templates
                      ? "Default job-offer email template will be used."
                      : "Enter the selected email template ID."
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Document Template ID"
                  disabled={sendOfferData.use_default_templates}
                  value={sendOfferData.document_template}
                  onChange={(event) =>
                    setSendOfferData((prev) => ({
                      ...prev,
                      document_template: event.target.value,
                    }))
                  }
                  helperText={
                    sendOfferData.use_default_templates
                      ? "Default job-offer letter template will be used."
                      : "Enter the selected document template ID."
                  }
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                p: 2,
                borderRadius: "16px",
                border: `1px dashed ${borderColor}`,
                backgroundColor: isDarkMode ? "rgba(255,255,255,0.02)" : "rgba(103,80,164,0.04)",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Sending the offer should mark the offer as <b>Sent</b>, persist rendered email and letter HTML, generate the PDF, and move the candidate into the <b>Job Offered</b> stage.
              </Typography>
            </Box>

            {sendOfferError ? (
              <Typography variant="body2" color="error">
                {sendOfferError}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <MDButton variant="outlined" color="dark" onClick={closeCollectionDialogs}>
            Close
          </MDButton>
          <MDButton variant="contained" color="success" onClick={handleSend}>
            Send Offer
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={historyDialog || documentsDialog || remindersDialog} onClose={closeCollectionDialogs} PaperProps={{ sx: { width: "760px", maxWidth: "94vw", borderRadius: "20px" } }}>
        <DialogTitle>
          {historyDialog ? "Offer Status History" : documentsDialog ? "Offer Documents" : "Offer Reminder Logs"}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={1.5}>
            {timelineItems.length ? (
              timelineItems.map((item, index) => (
                <Box key={item?.id || index} sx={{ p: 2, borderRadius: "16px", border: `1px solid ${borderColor}` }}>
                  <Typography fontWeight={700}>
                    {historyDialog
                      ? item?.status_display || getOfferStatusLabel(item?.status)
                      : documentsDialog
                      ? getDocumentTypeLabel(item)
                      : item?.reminder_type || "Reminder"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.25}>
                    {formatDateTime(item?.created_at || item?.updated_at || item?.sent_at)}
                  </Typography>
                  {documentsDialog ? (
                    <Box mt={1}>
                      <Typography variant="body2">Status: {item?.status_display || item?.status || "Pending"}</Typography>
                      {item?.verified !== undefined ? <Typography variant="body2">Verified: {item.verified ? "Yes" : "No"}</Typography> : null}
                      {item?.rejected_reason ? <Typography variant="body2">Rejected reason: {item.rejected_reason}</Typography> : null}
                    </Box>
                  ) : null}
                  {remindersDialog ? (
                    <Box mt={1}>
                      <Typography variant="body2">Category: {item?.category || item?.lifecycle_type || "Lifecycle reminder"}</Typography>
                      {item?.target_at ? <Typography variant="body2">Target: {formatDateTime(item.target_at)}</Typography> : null}
                    </Box>
                  ) : null}
                  {item?.reason ? <Typography variant="body2" mt={1}>Reason: {item.reason}</Typography> : null}
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No records are available yet.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="dark" onClick={closeCollectionDialogs}>Close</MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={reasonDialog.open} onClose={closeReasonDialog} PaperProps={{ sx: { width: "520px", maxWidth: "94vw", borderRadius: "20px" } }}>
        <DialogTitle>{reasonDialog.title}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="dark" onClick={closeReasonDialog}>Close</MDButton>
          <MDButton variant="contained" color="info" disabled={!reason.trim()} onClick={handleReasonSubmit}>Submit</MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={requestDocumentsDialog} onClose={closeCollectionDialogs} PaperProps={{ sx: { width: "620px", maxWidth: "94vw", borderRadius: "20px" } }}>
        <DialogTitle>Request Documents</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel id="document-request-select-label">Document Types</InputLabel>
              <Select
                labelId="document-request-select-label"
                multiple
                value={documentRequestData.document_type_ids}
                label="Document Types"
                renderValue={(selected) =>
                  documentTypes
                    .filter((docType) => selected.includes(docType.id))
                    .map((docType) => docType.name)
                    .join(", ")
                }
                onChange={(event) =>
                  setDocumentRequestData((prev) => ({
                    ...prev,
                    document_type_ids: event.target.value,
                  }))
                }
              >
                {documentTypes.map((docType) => (
                  <MenuItem key={docType.id} value={docType.id}>
                    <ListItemText primary={docType.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Document Deadline"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={documentRequestData.deadline}
              onChange={(event) =>
                setDocumentRequestData((prev) => ({
                  ...prev,
                  deadline: event.target.value,
                }))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="dark" onClick={closeCollectionDialogs}>Close</MDButton>
          <MDButton
            variant="contained"
            color="info"
            disabled={!documentRequestData.document_type_ids.length || !documentRequestData.deadline}
            onClick={handleRequestDocuments}
          >
            Request
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={uploadDocumentDialog} onClose={closeCollectionDialogs} PaperProps={{ sx: { width: "560px", maxWidth: "94vw", borderRadius: "20px" } }}>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel id="upload-document-type-label">Document Type</InputLabel>
              <Select
                labelId="upload-document-type-label"
                value={documentUploadData.document_type}
                label="Document Type"
                onChange={(event) =>
                  setDocumentUploadData((prev) => ({
                    ...prev,
                    document_type: event.target.value,
                  }))
                }
              >
                {getAvailableDocumentTypeOptions(selectedJobOffer).map((docType) => (
                  <MenuItem key={docType.id} value={docType.id}>
                    {docType.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="file"
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ accept: ".pdf,.jpg,.jpeg,.png" }}
              onChange={(event) =>
                setDocumentUploadData((prev) => ({
                  ...prev,
                  file: event.target.files?.[0] || null,
                }))
              }
            />
            {documentUploadData.file ? (
              <Typography variant="caption" color="text.secondary">
                Selected file: {documentUploadData.file.name}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="dark" onClick={closeCollectionDialogs}>Close</MDButton>
          <MDButton
            variant="contained"
            color="info"
            disabled={!documentUploadData.document_type || !documentUploadData.file}
            onClick={handleUploadDocument}
          >
            Upload
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={verifyDocumentDialog} onClose={closeCollectionDialogs} PaperProps={{ sx: { width: "560px", maxWidth: "94vw", borderRadius: "20px" } }}>
        <DialogTitle>Verify Document</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel id="verify-document-type-label">Document Type</InputLabel>
              <Select
                labelId="verify-document-type-label"
                value={documentVerifyData.document_type}
                label="Document Type"
                onChange={(event) =>
                  setDocumentVerifyData((prev) => ({
                    ...prev,
                    document_type: event.target.value,
                  }))
                }
              >
                {getAvailableDocumentTypeOptions(selectedJobOffer).map((docType) => (
                  <MenuItem key={docType.id} value={docType.id}>
                    {docType.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={documentVerifyData.verified}
                  onChange={(event) =>
                    setDocumentVerifyData((prev) => ({
                      ...prev,
                      verified: event.target.checked,
                      rejected_reason: event.target.checked ? "" : prev.rejected_reason,
                    }))
                  }
                />
              }
              label={documentVerifyData.verified ? "Mark as verified" : "Mark as rejected"}
            />

            {!documentVerifyData.verified ? (
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Rejected Reason"
                value={documentVerifyData.rejected_reason}
                onChange={(event) =>
                  setDocumentVerifyData((prev) => ({
                    ...prev,
                    rejected_reason: event.target.value,
                  }))
                }
              />
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="dark" onClick={closeCollectionDialogs}>Close</MDButton>
          <MDButton
            variant="contained"
            color="success"
            disabled={
              !documentVerifyData.document_type ||
              (!documentVerifyData.verified && !documentVerifyData.rejected_reason.trim())
            }
            onClick={handleVerifyDocument}
          >
            Submit
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
